import { useEffect, useState } from 'react'

import { useApi, useAsyncCallback, useNotification } from '../../../../hooks'

import { getAsyncEntityNextUpdatedData } from './useAsyncEntity.utils'

export function useAsyncEntity({
  entityId,
  getNextUpdatedEntityData = getAsyncEntityNextUpdatedData,
  getProfileData,
  updateProfileData,
}) {
  const [entityData, setEntityData] = useState(null)
  const [isInitiated, setInitiated] = useState(false)
  const [isLoading, setLoading] = useState(true)
  const [serverError, setServerError] = useState(null)

  const api = useApi()

  const { handleAddSuccessNotification } = useNotification()

  function resetStates() {
    setEntityData(null)
    setInitiated(false)
    setLoading(true)
    setServerError(null)
  }

  const loadData = useAsyncCallback(
    apiConfig => {
      return getProfileData(apiConfig)
        .then(({ data }) => {
          setEntityData(data)

          return data
        })
        .catch(error => {
          setServerError(error.response)
        })
        .finally(() => {
          setLoading(false)
          setInitiated(true)
        })
    },
    [getProfileData]
  )

  /**
   * Use this handler for complete reset states and reload entity data.
   */
  const handleEntityInit = useAsyncCallback(
    apiConfig => {
      resetStates()

      return loadData(apiConfig)
    },
    [loadData]
  )

  /**
   * Use this handler for reload entity data without resetting `isInitiated` state.
   */
  const handleEntityReload = useAsyncCallback(() => {
    setLoading(true)
    setServerError(false)

    return loadData()
  }, [loadData])

  /**
   * Use this handler to fold data saved through another endpoint into the
   * entity state without an extra request.
   */
  function handleMergeEntityData(data) {
    setEntityData(prevData => ({ ...prevData, ...data }))
  }

  async function handleEntitySilentReload() {
    return getProfileData()
      .then(({ data }) => {
        setEntityData(data)
        setServerError(null)

        return data
      })
      .catch(() => null)
  }

  const handleUpdateEntity = useAsyncCallback(
    (data, successTitle = 'Notification.CommonTitleSavedKey') => {
      setLoading(true)
      setServerError(false)

      return updateProfileData(data)
        .then(({ data }) => {
          setEntityData(prevData => getNextUpdatedEntityData(prevData, data))

          if (successTitle) {
            handleAddSuccessNotification(successTitle)
          }

          return data
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [getNextUpdatedEntityData, handleAddSuccessNotification, updateProfileData]
  )

  // Pass `entityId` when the profile stays mounted across entity navigation
  // (same route, different id) — the data is reloaded for the new entity.
  useEffect(() => {
    api.call(handleEntityInit)

    return () => {
      api.abort()
    }
  }, [entityId])

  return {
    entityData,
    handleEntityInit,
    handleEntityReload,
    handleEntitySilentReload,
    handleMergeEntityData,
    handleUpdateEntity,
    isInitiated,
    isLoading,
    serverError,
  }
}
